import { getAuthToken, removeAuthToken } from '../utils';
import { decodeRoles } from '../types/jwt';
import { ensureGuestToken } from './token-manager';
import { clearImpersonationSession, getImpersonationSession } from '@/lib/impersonation/session';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

/**
 * Thrown by ApiClient.request() for any non-2xx HTTP response.
 * Preserves the HTTP status code and the parsed response body so callers
 * can branch on specific codes or server-supplied flags (e.g. requiresOAuth).
 */
export class HttpError extends Error {
  readonly status: number;
  readonly data: Record<string, unknown>;

  constructor(message: string, status: number, data: Record<string, unknown> = {}) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

/**
 * Safely converts a `HeadersInit` (which may be a `Headers` instance,
 * `[string, string][]`, or `Record<string, string>`) into a plain
 * `Record<string, string>`. This avoids unsafe casts that can silently drop
 * headers when the caller passes a non-object form.
 */
const headersToRecord = (headers: HeadersInit | undefined | null): Record<string, string> => {
  if (!headers) return {};
  const result: Record<string, string> = {};
  // `new Headers()` accepts all three valid forms and provides uniform
  // iteration via `.forEach()`.
  new Headers(headers).forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

/**
 * Extracts a human-readable error message from the parsed JSON body of a
 * non-2xx API response.
 *
 * nopCommerce returns validation errors in different shapes depending on the
 * endpoint:
 *   - Object with `.message` or `.error` field  →  use that string
 *   - Array of strings (e.g. ["The specified email already exists"])  →  join them
 *   - Anything else  →  fall back to a generic status-code message
 */
const extractErrorMessage = (errorData: unknown, fallbackStatus: number): string => {
  if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
    const obj = errorData as Record<string, unknown>;
    const msg = (obj.message as string | undefined) || (obj.error as string | undefined);
    if (msg) return msg;
  }
  if (Array.isArray(errorData)) {
    const lines = errorData.filter((s): s is string => typeof s === 'string');
    if (lines.length > 0) return lines.join('. ');
  }
  return `HTTP error! status: ${fallbackStatus}`;
};

/**
 * Extracts a human-readable message from a failed API call.
 *
 * The API client throws `HttpError`, which carries the parsed response body on
 * `.data`. Code that reads `error.response.data.message` (an Axios shape) will
 * therefore always fall through to a generic fallback and hide the server's
 * actual message. Use this helper instead.
 */
export const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof HttpError) {
    const fromData = (error.data?.message as string | undefined) ?? (error.data?.error as string | undefined);
    if (fromData) return fromData;
    if (error.message) return error.message;
    return fallback;
  }

  if (error instanceof Error && error.message) return error.message;

  return fallback;
};

export class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T>(endpoint: string, options: RequestInit & { _isRetry?: boolean } = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const authToken = getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headersToRecord(options.headers),
    };

    // Only attach the stored marketplace token when the caller has not already
    // supplied an Authorization header (e.g. Shopify session token endpoints).
    if (authToken && !headers['Authorization']) {
      headers['Authorization'] = authToken;
    }

    // Spread options first, then override headers with the merged set that
    // includes the auth token. This ensures caller-supplied headers (e.g.
    // Content-Type for binary uploads) are merged in, but never clobber auth.
    const config: RequestInit = {
      ...options,
      headers,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = extractErrorMessage(errorData, response.status);
      // 401 interceptor: if the token expired between the ensureGuestToken()
      // check and the server receiving the request, attempt a single silent
      // guest-token refresh and retry. Only retry once to avoid infinite loops.
      if (response.status === 401 && !options._isRetry) {
        const currentToken = getAuthToken();
        // Check role claims directly - isUser()/isGuest() return false for
        // expired tokens, which would misidentify expired guest tokens as
        // user tokens and incorrectly redirect to /login.
        if (currentToken && !decodeRoles(currentToken).includes('Guest')) {
          // Impersonation session: never redirect to /login — the banner owns
          // the expired-session UX (see ImpersonationBanner).
          const impersonationSession = getImpersonationSession();
          if (impersonationSession) {
            // The server refuses an impersonation token once its session has
            // been finished (e.g. the admin ended the session in another tab).
            // Drop the token here so the storefront stops acting as the
            // impersonated customer, and fall back to a guest token instead of
            // redirecting the admin to /login.
            clearImpersonationSession();
            removeAuthToken();
            await ensureGuestToken();
            const revokedHeaders: Record<string, string> = {
              'Content-Type': 'application/json',
              ...headersToRecord(options.headers),
            };
            const guestToken = getAuthToken();
            if (guestToken && !revokedHeaders['Authorization']) {
              revokedHeaders['Authorization'] = guestToken;
            }
            const revokedConfig: RequestInit & { _isRetry?: boolean } = {
              ...options,
              headers: revokedHeaders,
              _isRetry: true,
            };
            const revokedResponse = await fetch(url, revokedConfig);
            if (revokedResponse.ok) {
              const revokedData = await revokedResponse.json();
              return { data: revokedData as T, status: revokedResponse.status };
            }
            throw new HttpError(
              extractErrorMessage(
                await revokedResponse.json().catch(() => ({})),
                revokedResponse.status,
              ),
              revokedResponse.status,
            );
          }
          // Authenticated user's token expired mid-request - redirect to login
          removeAuthToken();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          throw new HttpError(
            errorMessage,
            response.status,
            errorData as Record<string, unknown>,
          );
        }

        // Guest token expired or was rejected - refresh and retry once
        removeAuthToken();
        await ensureGuestToken();

        // Rebuild headers with the fresh token
        const freshToken = getAuthToken();
        const retryHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
          ...headersToRecord(options.headers),
        };
        if (freshToken && !retryHeaders['Authorization']) {
          retryHeaders['Authorization'] = freshToken;
        }

        const retryConfig: RequestInit & { _isRetry?: boolean } = {
          ...options,
          headers: retryHeaders,
          _isRetry: true,
        };

        return this.request<T>(endpoint, retryConfig);
      }
      throw new HttpError(
        errorMessage,
        response.status,
        errorData as Record<string, unknown>,
      );
    }

    const data = await response.json();

    return {
      data,
      status: response.status,
    };
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async postRaw<T>(endpoint: string, body: BodyInit, contentType: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body,
      headers: { 'Content-Type': contentType },
    });
  }

  /**
   * POST a FormData body. Does NOT set Content-Type so the browser supplies
   * the multipart/form-data boundary automatically.
   */
  async postForm<T>(endpoint: string, body: FormData): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const authToken = getAuthToken();
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = authToken;
    const response = await fetch(url, { method: 'POST', body, headers: headers as Record<string, string> });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = extractErrorMessage(errorData, response.status);
      // 401 interceptor for form posts - refresh guest token and retry once
      if (response.status === 401) {
        const currentToken = getAuthToken();
        // Check role claims directly - see request() for rationale.
        if (currentToken && !decodeRoles(currentToken).includes('Guest')) {
          removeAuthToken();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          throw new HttpError(
            errorMessage,
            response.status,
            errorData as Record<string, unknown>,
          );
        }
        removeAuthToken();
        await ensureGuestToken();
        const freshToken = getAuthToken();
        const retryHeaders: Record<string, string> = {};
        if (freshToken) retryHeaders['Authorization'] = freshToken;
        const retryResponse = await fetch(url, { method: 'POST', body, headers: retryHeaders });
        if (retryResponse.ok) {
          return { data: await retryResponse.json() as T, status: retryResponse.status };
        }
        const retryErrorData = await retryResponse.json().catch(() => ({}));
        const retryErrorMessage = extractErrorMessage(retryErrorData, retryResponse.status);
        throw new HttpError(
          retryErrorMessage,
          retryResponse.status,
          retryErrorData as Record<string, unknown>,
        );
      }
      throw new HttpError(
        errorMessage,
        response.status,
        errorData as Record<string, unknown>,
      );
    }
    return { data: await response.json() as T, status: response.status };
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }
}


